import { useState } from "react"
import { useAppToast } from "@ui-seeker/app"
import { postSubmit, deleteSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { isLoadingSchedules, schedulesTableData } from "../@signals"


export default function useScheduleMutations() {
    const { showToast } = useAppToast()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const createSchedule = async (scheduleData) => {
        setIsSubmitting(true)
        try {
            const result = await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/schedules`, scheduleData)
            showToast.success('Schedule created successfully')
            isLoadingSchedules.value = true
            return result
        } catch (err) {
            console.error('Failed to create schedule:', err)
            showToast.error(err?.message || 'Failed to create schedule')
            throw err
        } finally {
            setIsSubmitting(false)
        }
    }

    const updateSchedule = async (id_schedule, updateData) => {
        setIsSubmitting(true)
        try {
            // Use fetch directly with PATCH method since patchSubmit doesn't exist
            const response = await fetch(`${BACKEND_APIS.BATCH_ADMIN}/admin/schedules/${id_schedule}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updateData)
            })

            if (!response.ok) {
                const error = await response.json()
                throw new Error(error.message || 'Failed to update schedule')
            }

            const result = await response.json()
            showToast.success('Schedule updated successfully')
            isLoadingSchedules.value = true
            return result
        } catch (err) {
            console.error('Failed to update schedule:', err)
            showToast.error(err?.message || 'Failed to update schedule')
            throw err
        } finally {
            setIsSubmitting(false)
        }
    }

    const deleteSchedule = async (id_schedule) => {
        setIsSubmitting(true)
        try {
            await deleteSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/schedules/${id_schedule}`)

            // Remove from table data immediately
            schedulesTableData.value = schedulesTableData.value.filter(
                s => s.idSchedule !== id_schedule
            )

            showToast.success('Schedule deleted successfully')
        } catch (err) {
            console.error('Failed to delete schedule:', err)
            showToast.error(err?.message || 'Failed to delete schedule')
            throw err
        } finally {
            setIsSubmitting(false)
        }
    }

    const toggleActive = async (id_schedule) => {
        setIsSubmitting(true)
        try {
            const result = await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/schedules/${id_schedule}/toggle-active`, {})

            // Update in table data immediately
            schedulesTableData.value = schedulesTableData.value.map(s =>
                s.idSchedule === id_schedule
                    ? { ...s, isActive: result.isActive }
                    : s
            )

            showToast.success(result.isActive ? 'Schedule activated' : 'Schedule deactivated')
        } catch (err) {
            console.error('Failed to toggle schedule:', err)
            showToast.error(err?.message || 'Failed to toggle schedule')
            throw err
        } finally {
            setIsSubmitting(false)
        }
    }

    const runNow = async (id_schedule) => {
        setIsSubmitting(true)
        try {
            const result = await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/schedules/${id_schedule}/run-now`, {})
            showToast.success(`Batch "${result.batchName}" created successfully`)
            return result
        } catch (err) {
            console.error('Failed to run schedule:', err)
            showToast.error(err?.message || 'Failed to run schedule')
            throw err
        } finally {
            setIsSubmitting(false)
        }
    }

    return {
        isSubmitting,
        createSchedule,
        updateSchedule,
        deleteSchedule,
        toggleActive,
        runNow
    }
}
